import { useQuery } from '@vue/apollo-composable';
import { GET_SCHOOL_QUERY } from '@/graphql';
import type { School } from '@/types/composition-functions';
import { useUserStore } from '@/store/userStore';

/**
 * Scrollbar visibility: per-school default, per-user override.
 *
 * The platform hides scrollbars globally (main.css hides every
 * ::-webkit-scrollbar, and the dashboard shells add .no-scrollbar to their
 * scroll containers). Schools that asked for visible scrollbars get them back
 * via settings.show_scrollbars; on top of that, each user may pin their own
 * choice from My Account — the personal preference always beats the school
 * default. When the winner is "show", the `show-scrollbars` class goes on
 * <html> and the overrides in main.css re-enable scrollbars everywhere —
 * admin and student portals alike.
 *
 * Call once from App.vue. Presentation-only; nothing here is security.
 */

export const SHOW_SCROLLBARS_CLASS = 'show-scrollbars';

/** 'show' | 'hide' pins the user's choice; null means follow the school. */
export type ScrollbarPreference = 'show' | 'hide' | null;

const PREF_KEY_PREFIX = 'scrollbar-preference:';

// localStorage is not reactive; bumping this after every write makes the
// preference computed re-read it. Module-level so every consumer agrees.
const prefVersion = ref(0);

const storageKey = (userId: string) => `${PREF_KEY_PREFIX}${userId}`;

export function useScrollbarVisibility() {
  const userStore = useUserStore();

  // Same cast the router guard and useAiAssistantAccess use: the generated
  // `me` union doesn't carry __typename on every branch.
  const role = computed(() => (userStore.currentUser as { __typename?: string } | null)?.__typename);
  const isStudent = computed(() => role.value === 'Student');
  const isStaff = computed(() => role.value === 'User');

  // Staff read the flag from getSchool. Reusing the dashboard's own query
  // document means Apollo's cache-first policy dedupes this into the request
  // the shell already makes — and a settings save (which returns {id,
  // settings}) live-updates it through the normalized cache.
  const { result: schoolResult } = useQuery<{ getSchool: School }>(
    GET_SCHOOL_QUERY,
    null,
    () => ({ enabled: isStaff.value }),
  );

  // What the school decided for everyone, before any personal override.
  const schoolDefault = computed(() => {
    if (isStaff.value) {
      const settings = schoolResult.value?.getSchool?.settings as { show_scrollbars?: boolean } | null | undefined;
      return !!settings?.show_scrollbars;
    }
    if (isStudent.value) {
      // Students carry the school (with its settings JSON) on the me payload;
      // fall back to the current classroom section's school, which selects
      // the same blob.
      const me = userStore.currentUser as {
        school?: { settings?: { show_scrollbars?: boolean } | null } | null;
        currentClassroomSection?: { school?: { settings?: { show_scrollbars?: boolean } | null } | null } | null;
      } | null;
      const school = me?.school ?? me?.currentClassroomSection?.school;
      return !!school?.settings?.show_scrollbars;
    }
    // Logged out / other roles: leave the platform default (hidden) alone.
    return false;
  });

  const userId = computed(() => {
    const id = (userStore.currentUser as { id?: string | number } | null)?.id;
    return id === undefined || id === null ? null : String(id);
  });

  // Keyed per user id so two accounts on one browser never share a choice.
  const preference = computed<ScrollbarPreference>(() => {
    void prefVersion.value;
    if (!userId.value) return null;
    const raw = localStorage.getItem(storageKey(userId.value));
    return raw === 'show' || raw === 'hide' ? raw : null;
  });

  function setPreference(value: ScrollbarPreference) {
    if (!userId.value) return;
    if (value) {
      localStorage.setItem(storageKey(userId.value), value);
    } else {
      localStorage.removeItem(storageKey(userId.value));
    }
    prefVersion.value += 1;
  }

  const showScrollbars = computed(() => {
    if (preference.value) return preference.value === 'show';
    return schoolDefault.value;
  });

  // Logout flows through here too: currentUser clears, the flag computes
  // false, and the class comes off — no explicit teardown needed.
  watch(
    showScrollbars,
    (on) => {
      document.documentElement.classList.toggle(SHOW_SCROLLBARS_CLASS, on);
    },
    { immediate: true },
  );

  return {
    showScrollbars, schoolDefault, preference, setPreference,
  };
}
